import { connect, type ConnectedProps } from 'react-redux';
import * as yup from 'yup';
import { type SubmitHandler } from 'react-hook-form';
import { useRef } from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { isAppLoading } from '@/core/features/loader';
import { openModal } from '@/core/features/modal';
import SelectField from '@/components/SelectField';
import Form, { type FormElement } from '@/components/Form';
import { updateUserRole } from '@/slices/user';
import { roleOptions, updateMember } from '@/slices/members';
import { type RootState } from '@/store';
import { normalizeSimpleFormOptions, useYupValidationResolver } from '@/utils';
import { type DrawerContentProps } from '../../../state';
import { DrawerLayout } from '../../layouts';
import styles from './styles';

const validationSchema = yup.object({
  role_id: yup.string().required('Role is required'),
});

type FormValues = {
  role_id?: string;
  is_public?: boolean;
};

type Props = DrawerContentProps & ConnectedProps<typeof connector>;

const ViewMemberDrawer = ({
  closeDrawer,
  drawerData: { member, propertyId },
  roleOptions,
  isAppLoading,
  updateUserRole,
  updateMember,
  openModal,
}: Props) => {
  const formRef = useRef<FormElement>(null);
  const resolver = useYupValidationResolver(validationSchema);

  const handleOpenFileUpload = () => {
    openModal({
      modalType: 'fileUpload',
      modalData: { member_id: member.user_id, propertyId },
    });
  };

  const defaultValues: FormValues = {
    role_id: String(roleOptions.find((role) => role.name === member.role)?.id),
    is_public: member.is_public,
  };

  const handleAccept = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const onSubmit: SubmitHandler<FormValues> = ({ role_id, is_public }) => {
    updateMember({
      memberId: member.user_id,
      is_public: Boolean(is_public),
    });
    updateUserRole({
      propertyId,
      user_id: member.user_id,
      role_id: Number(role_id),
    });
  };

  return (
    <DrawerLayout
      closeDrawer={closeDrawer}
      title="Update Member"
      onAccept={handleAccept}
      acceptText={isAppLoading ? 'Saving...' : 'Save'}
      acceptDisabled={isAppLoading}
      onCancel={closeDrawer}
      cancelText="Cancel"
    >
      <Form
        ref={formRef}
        sx={styles.form}
        resolver={resolver}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
      >
        <Box sx={styles.header}>
          <Avatar
            src={member?.avatar}
            alt={member?.full_name}
            sx={styles.avatar}
            onClick={handleOpenFileUpload}
          />

          <Typography sx={styles.email} variant="body2">
            {member?.email}
          </Typography>
        </Box>

        <SelectField
          name="role_id"
          label="Role"
          options={normalizeSimpleFormOptions(roleOptions)}
        />

        <SelectField
          name="is_public"
          label="Commentator"
          options={normalizeSimpleFormOptions([
            { id: 1, name: 'Yes', boolean: true },
            { id: 0, name: 'No', boolean: false },
          ])}
        />
      </Form>
    </DrawerLayout>
  );
};

const connector = connect(
  (state: RootState) => ({
    roleOptions: roleOptions(state),
    isAppLoading: isAppLoading(state),
  }),
  { updateUserRole, updateMember, openModal },
);

export default connector(ViewMemberDrawer);
